import { createModel } from '@rematch/core';
import { ConfigProvider } from 'antd';
import {
  EmailOtpRequestPayload,
  EmailOtpRequestResponse,
  EmailOtpVerifyPayload,
  EventType,
  LoginForm,
  SsoLoginParams,
  SsoLoginResponse,
  ApplyAccessRequest,
  SelfRegisterRequest,
  Theme,
  ThemeObject,
  User,
  QuickbooksLoginParams,
  QuickbooksLoginResponse,
} from 'domains/appDomain';
import { NetworkMember } from 'domains/networkDomain';
import { SkuType } from 'domains/skuDomain';
import accountService from 'services/accountService';
import jwt_decode from 'jwt-decode';
import appService, {
  OAUTH_REDIRECT_SENTINEL,
  persistAuthTokenForBrowserSession,
} from 'services/appService';
import networkService from 'services/networkService';
import orgService from 'services/orgService';
import skuService from 'services/skuService';
import themeService from 'services/themeService';
import { addZenDesk } from 'utils/misc';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import {
  cacheUnitForOrg,
  cachedUnitForOrg,
  readStoredUnit,
  setEffectiveUnit,
  storeUnit,
} from 'utils/measurement';
import type { RootModel } from '.';
import { baseReducers, BaseState } from './baseReducers';
import { clearCanvaSession, markCanvaSession } from 'utils/canvaSession';

/** Active tenant: switched org in localStorage wins over JWT home org. */
function resolveActiveOrgId(user: User | null | undefined): number {
  const storedOrgId = Number(localStorage.getItem('org')) || 0;
  return storedOrgId || user?.orgId || 0;
}

interface AppState extends BaseState {
  filterForceHidden: boolean;
  user: User | null;
  orgNetworks: NetworkMember[] | undefined;
  skuTypes: SkuType[];
  orgId: number;
  selectedSkuType: SkuType | null;
  theme: Theme;
  themeData: ThemeObject;
  loading: boolean;
  isUploading: boolean;
  /**
   * Measurement system sizes and weights are rendered in. Defaults to the active org's setting and
   * can be switched by the user at any time; storage stays imperial, so it never moves a price.
   */
  measurementUnit: MeasurementUnitEnum;
  /**
   * Whether the org's answer has actually arrived.
   *
   * False during the first moments of a session, when `measurementUnit` is still the cached or
   * default guess. Forms that take measurements wait for this rather than render a field whose
   * label is about to change under what someone is typing into it.
   */
  measurementUnitReady: boolean;
}

const loggedInUser = appService.getUserFromJwtToken();
export const app = createModel<RootModel>()({
  state: {
    filterForceHidden: false,
    user: loggedInUser,
    loading: false,
    skuTypes: [],
    orgId: Number(localStorage.getItem('org')) || loggedInUser?.orgId || 0,
    selectedSkuType: null,
    orgNetworks: undefined,
    theme: 'default',
    themeData: themeService.getDefaultThemeObject(),
    isUploading: false,
    // This person's own choice if they have one, else what this org resolved to last time, so
    // a reload does not render in the wrong unit and then flip. Both are replaced by the
    // server's answer on hydration when nobody has chosen; imperial is what every stored value
    // already is, so it is the safe thing to render with neither.
    measurementUnit:
      readStoredUnit() ??
      cachedUnitForOrg(Number(localStorage.getItem('org')) || loggedInUser?.orgId) ??
      MeasurementUnitEnum.IMPERIAL,
    measurementUnitReady: false,
  } as unknown as AppState,
  reducers: {
    ...baseReducers<AppState>(),
    reset: (state: AppState) => {
      state.user = null;
      state.skuTypes = [];
      state.orgId = 0;
      state.selectedSkuType = null;
      return state;
    },
  },
  effects: (dispatch) => {
    const { app } = dispatch;
    return {
      async login(
        payload: LoginForm
      ): Promise<User | typeof OAUTH_REDIRECT_SENTINEL> {
        return appService.login(payload);
      },

      async requestEmailOtp(
        payload: EmailOtpRequestPayload
      ): Promise<EmailOtpRequestResponse> {
        return appService.requestEmailOtp(payload);
      },

      async verifyEmailOtp(
        payload: EmailOtpVerifyPayload
      ): Promise<
        { user: User; createdUser: boolean } | typeof OAUTH_REDIRECT_SENTINEL
      > {
        return appService.verifyEmailOtp(payload);
      },

      /**
       * Loads org list, networks, and other global state after login or Canva SSO exchange.
       * App mount uses this for existing sessions; Canva exchange calls it after the JWT is stored.
       */
      /**
       * Switches the measurement system for this user and remembers the choice.
       *
       * Display only - the API keeps storing and returning imperial, so this never changes a price.
       */
      /**
       * Switches the system this person reads in, and remembers it.
       *
       * Theirs, and it outranks the org's default from here on: the org setting answers which
       * system its data is entered and stored in, this answers which one they want to look at.
       * Someone working across a US and a Japanese supplier picks once.
       *
       * Display only - storage stays imperial - so it never moves a price.
       */
      switchMeasurementUnit(unit: MeasurementUnitEnum) {
        storeUnit(unit);
        setEffectiveUnit(unit);
        dispatch.app.setState({ measurementUnit: unit, measurementUnitReady: true });
      },

      /**
       * Applies the active org's default unit, unless the user has already chosen one for
       * themselves - their own choice outranks the org setting.
       *
       * A failure here is not worth blocking the session for: imperial is what every stored value
       * already is, so falling back to it is correct, just not the org's preference.
       */
      /**
       * Settles which measurement system to show:
       *
       * 1. what this person picked for themselves, which outranks everything and is remembered
       * 2. the org's own override, when it has set one
       * 3. the org's locale - the normal case
       * 4. its country, for an org whose locale has never been filled in
       * 5. imperial, which is what every stored value already is
       *
       * Steps 2 to 5 are the org's default and are settled by the API in one call, because only
       * the server can read the org row. A person who has chosen never causes that call to
       * decide anything.
       *
       * None of this changes what a save writes. The unit a record is entered and stored in
       * belongs to the org that owns it, and a form that converts its values says so per request.
       */
      async loadOrgMeasurementUnit(orgId: number): Promise<void> {
        // Always asked, even when this person has chosen for themselves. Their choice settles
        // what they read in; it says nothing about which system the org's data is in, and that
        // second answer is needed whether or not they have expressed a preference.
        let orgUnit = cachedUnitForOrg(orgId) ?? MeasurementUnitEnum.IMPERIAL;
        try {
          orgUnit =
            (await orgService.getDefaultMeasurementUnit(orgId)) ??
            MeasurementUnitEnum.IMPERIAL;
          cacheUnitForOrg(orgId, orgUnit);
        } catch (e) {
          // Keep whatever the cache said rather than snapping to imperial: a metric org that
          // loses this one call is better served by its last known answer than by the default.
          console.warn('could not settle the measurement unit, using the last known one', e);
        }
        const shown = readStoredUnit() ?? orgUnit;
        setEffectiveUnit(shown);
        // Ready either way. The forms are waiting on an answer, not on a successful call, and
        // blocking them forever on a failed one is worse than working from the cache.
        dispatch.app.setState({
          measurementUnit: shown,
          measurementUnitReady: true,
        });
      },

      async hydrateAuthenticatedSession(user: User): Promise<void> {
        await app.loadLoginUser(user);
        const orgId = resolveActiveOrgId(user);

        dispatch.app.eventSource();
        dispatch.industryAvg.loadElementMarkups();
        dispatch.app.loadOrgNetworks(orgId);
        dispatch.org.getInternalDistributionAddresses(orgId);
        dispatch.gallery.getGalleryLabels();

        if (window.location.pathname.indexOf('/platform') === -1) {
          await dispatch.org.getOrgs();
          await dispatch.companyGallery.getSkuTypes();
        } else {
          await dispatch.app.getSkuTypes();
        }

        await dispatch.app.loadOrgMeasurementUnit(orgId);

        addZenDesk(user);
      },

      /** Finishes Canva seamless SSO: exchange one-time code, persist JWT, hydrate profile. */
      async completeCanvaSsoExchange(code: string): Promise<User> {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('org');
        app.reset();

        // Remembered for the tab: from here on this buyer shops like an ordinary user, and the
        // parked design id that says so is only ever on the first link.
        markCanvaSession();

        const { token } = await appService.exchangeCanvaSso(code);
        persistAuthTokenForBrowserSession(token);
        const user = jwt_decode<User>(token);
        await app.hydrateAuthenticatedSession(user);
        return user;
      },

      async loginWithSso(
        payload: SsoLoginParams
      ): Promise<{ user: User | null; response: SsoLoginResponse }> {
        const result = await appService.ssoLogin(payload);
        console.log('SSO Login payload : ', payload, ' , Result:', result);
        return result;
      },

      async selfRegister(
        payload: SelfRegisterRequest
      ): Promise<{ user: User | null; response: SsoLoginResponse }> {
        const result = await appService.selfRegister(payload);
        return result;
      },

      async loginQuickbooks(
        payload: QuickbooksLoginParams
      ): Promise<QuickbooksLoginResponse> {
        const result = await appService.quickbooksLogin(payload);
        return result;
      },

      async applyAccess(payload: ApplyAccessRequest): Promise<void> {
        await appService.applyAccess(payload);
      },

      async loadLoginUser(payload: User, state) {
        const user = payload;
        const userDetail = await accountService.getUserById({
          id: user?.userId,
        } as any);

        if (user) {
          const newUser: User = { ...user };
          newUser.firstName = userDetail?.firstName;
          newUser.lastName = userDetail?.lastName;
          newUser.manageOrgs = userDetail?.manageOrgs;
          newUser.email = userDetail?.email;
          newUser.defaultContactId = userDetail?.defaultContactId;
          newUser.allowSwitchToSourcer = userDetail?.allowSwitchToSourcer;
          newUser.allowSwitchToSupplier = userDetail?.allowSwitchToSupplier;
          newUser.allowAdjustJob = userDetail?.allowAdjustJob;
          newUser.canvaUserId = userDetail?.canvaUserId;
          // Preserve SSO flag if it was set
          if (user.isSsoUser) {
            newUser.isSsoUser = true;
          }
          const defaultContact = userDetail?.contacts.find(
            (c) => c.id === userDetail.defaultContactId
          );
          if (defaultContact) {
            newUser.defaultAddress = defaultContact.addresses.find(
              (a) => a.id === defaultContact.defaultAddressId
            );
          }
          const storedOrgId = Number(localStorage.getItem('org')) || 0;
          const orgId = storedOrgId || newUser?.orgId || 0;
          if (!storedOrgId && newUser?.orgId != null) {
            localStorage.setItem('org', String(newUser.orgId));
          }
          const hasLinkedToQuickBooksResponse =
            await appService.checkHasLinkedToQuickBooks({
              orgId,
            });
          newUser.hasLinkedToQuickBooks =
            hasLinkedToQuickBooksResponse.hasLinked;

          app.setState({
            user: { ...newUser },
            orgId,
          });
        }
      },

      async logout() {
        localStorage.removeItem('Authorization');
        localStorage.removeItem('org');
        // Otherwise an ordinary login in the same tab would inherit the Canva shop behaviour.
        clearCanvaSession();
        app.reset();
      },

      /**
       * Canva seamless SSO: replace synthetic email + placeholder name on the current user with real
       * values. Server enforces "ask once" — succeeds only while user.email is still synthetic.
       *
       * Also refreshes the cached contact list because the org-api mirrors the new name+email onto
       * the user's default contact, and the expert "Arrival Date" step renders the "To" name from
       * `state.jobConfig.userAvlContacts`. Without this refresh the field would keep showing the
       * placeholder "Canva User, Canva" until the next page load.
       */
      async updateCanvaProfile(
        payload: { firstName: string; lastName: string; email: string },
        state
      ): Promise<void> {
        await accountService.updateCanvaProfile(payload);
        if (state.app.user) {
          await app.loadLoginUser(state.app.user);
        }
        try {
          await dispatch.jobConfig.getUserAvlContacts(undefined);
        } catch {
          // best-effort: stale contacts are not a hard failure for the profile update
        }
      },

      async getSkuTypes(): Promise<void> {
        try {
          const resp = await skuService.getSkuTypes();
          app.setState({
            skuTypes: resp,
          });
        } catch (e) {
          app.setState({
            skuTypes: [],
          });
        }
      },

      async switchTheme(theme: Theme) {
        const data = await themeService.getThemeObject(theme);
        app.setState({
          theme,
          themeData: data,
        });
        ConfigProvider.config({
          theme: {
            primaryColor: data.primaryColor,
          },
        });
      },

      async switchOrg(payload: number | string): Promise<void> {
        localStorage.setItem('org', String(payload));
        app.setState({ orgId: Number(payload) });
        dispatch.org.toggleSider(false);

        // After switch org, should:
        // 1. keep the selected tab unchanged.
        // 2. discard any nested status of the selected tab.
        // For example, If we're in some org's contact address page before switching
        // After switching, we should return back to the org list page
        const pathes = window.location.pathname.split('/');
        window.location.pathname = pathes.slice(0, 3).join('/');
      },

      async switchStoreOrg(payload: number): Promise<void> {
        localStorage.setItem('org', String(payload));
        window.location.href = `${window.location.origin}/workspace`;
      },

      showLoading() {
        app.setState({
          loading: true,
        });
      },

      async loadOrgNetworks(payload: number) {
        const networks = await networkService.getNetworksByMemberId(payload);
        app.setState({
          orgNetworks: networks,
        });
      },

      hideLoading() {
        app.setState({
          loading: false,
        });
      },

      eventSource() {
        try {
          const event = appService.eventSource();
          event.addEventListener<'message'>(
            EventType.PING as unknown as 'message',
            (e) => {
              const res = JSON.parse(e.data);
              if (res.code === 0) {
                console.log(EventType.PING, res.data);
              }
            }
          );
          event.addEventListener<'message'>(
            EventType.NOTIFICATION as unknown as 'message',
            (e) => {
              const res = JSON.parse(e.data);
              if (res.code === 0) {
                console.log(EventType.NOTIFICATION, res.data);
                dispatch.alert.setState({
                  alertsInfo: res.data,
                });
              }
            }
          );
          event.addEventListener<'message'>(
            EventType.WORKSPACE_COUNT as unknown as 'message',
            (e) => {
              const res = JSON.parse(e.data);
              if (res.code === 0) {
                console.log(EventType.WORKSPACE_COUNT, res.data);
              }
            }
          );
          event.addEventListener<'message'>(
            EventType.JOB as unknown as 'message',
            (e) => {
              const res = JSON.parse(e.data);
              if (res.code === 0) {
                console.log(EventType.JOB, res.data);
              }
            }
          );
        } catch (e) {
          console.log(e);
        }
      },
    };
  },
});
